import React, { useState, Fragment } from "react";
import { PlxsTextField } from "@plexlogic/plexus-ui";
import WizardFooterNav from "../WizardFooterNav";
import WizardStepHeader from "../WizardStepHeader";

interface SimpleEntriesStepProps {
  initialCount?: number;
  onBack: () => void;
  onContinue: (count: number) => void;
  isDisabled?: boolean;
}

// Utility function to handle number input
const sanitizeNumber = (value: string): string => {
  // Strip non-digits
  const digits = value.replace(/\D/g, "");

  // Remove leading zeros but keep single '0'
  const withoutLeadingZeros = digits.replace(/^0+/, "") || "0";

  // Clamp to reasonable range (0 to 1 billion)
  const num = parseInt(withoutLeadingZeros, 10);
  if (num > 1e9) {
    return "1000000000";
  }

  return withoutLeadingZeros;
};

const SimpleEntriesStep: React.FC<SimpleEntriesStepProps> = ({
  initialCount = 0,
  onBack,
  onContinue,
  isDisabled = false,
}) => {
  const [entryCount, setEntryCount] = useState<string>(
    initialCount > 0 ? initialCount.toString() : ""
  );
  const [showErrors, setShowErrors] = useState<boolean>(false);

  // Validate the current value
  const getErrorMessage = (value: string): string => {
    const num = parseInt(value, 10);
    if (!value || isNaN(num) || num <= 1) {
      return "A random draw requires at least 2 entries";
    }

    return "";
  };

  const errorMessage = getErrorMessage(entryCount);
  const hasError = showErrors && !!errorMessage;

  const handleInputChange = (value: string) => {
    const sanitized = sanitizeNumber(value);
    setEntryCount(sanitized);
    // Clear errors when user starts typing
    if (showErrors) {
      setShowErrors(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === "Enter" &&
      !errorMessage &&
      entryCount &&
      parseInt(entryCount, 10) > 1
    ) {
      handleContinue();
    }
  };

  const handleContinue = () => {
    const count = parseInt(entryCount, 10);
    if (errorMessage || count <= 1) {
      setShowErrors(true);
      return;
    }
    onContinue(count);
  };

  return (
    <Fragment>
      <div
        style={{
          maxWidth: 800,
          margin: "0 auto",
          padding: "0 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {/* Title */}
        <WizardStepHeader
          title="How many entries?"
          description="Enter your total once entries have closed, so the number's final. We'll pick the winning numbers for you to match to your list."
        />

        {/* Numeric Input */}
        <div style={{ maxWidth: 300 }}>
          <PlxsTextField
            dataTestId="entry-count-input"
            id="entry-count-input"
            type="text"
            label="Number of entries"
            value={entryCount}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. 5000"
            styleOverrides={{ hidePlaceholderEllipsis: true }}
            isDisabled={isDisabled}
            hasError={hasError}
            errorMessage={hasError ? errorMessage : undefined}
          />
        </div>

        {/* Footer Navigation */}
        <WizardFooterNav
          backButton={{
            dataTestId: "back-simple-entries",
            label: "Back",
            variant: "secondary",
            onClick: onBack,
          }}
          continueButton={{
            dataTestId: "next-simple-entries",
            label: "Next",
            variant: "primary",
            onClick: handleContinue,
          }}
        />
      </div>
    </Fragment>
  );
};

export default SimpleEntriesStep;
